import React from 'react';
import Button from '@app/react-common/components/button/Button';
import Tooltip from '@app/react-common/components/tooltip/Tooltip';
import { PriceRangeTier } from '../BulkEditModal';
import { ShippingRatesFormData } from './ShippingRatesEditView';
import { CollapsibleSection } from '../components';
import { BasePreviewViewProps, SelectedMethodInfo } from './types';

interface ShippingRatesPreviewViewProps extends BasePreviewViewProps {
  formData: ShippingRatesFormData;
  currencySign: string;
}

export const ShippingRatesPreviewView = ({
  onBack,
  onClose,
  onSaveChanges,
  formData,
  selectedMethods,
  currencySign,
}: ShippingRatesPreviewViewProps) => {
  const adjustRate = (value: number | string | null | undefined): number | null => {
    if (value === null || value === undefined) {
      return null;
    }
    const numValue = Number(value);
    // If keepFreeShippingFree is true and rate is 0, don't adjust
    if (formData.keepFreeShippingFree && numValue === 0) {
      return 0;
    }
    const newValue = numValue + formData.adjustBy;
    // Don't let rate go below 0
    if (newValue < 0) {
      return 0;
    }
    return newValue;
  };

  const formatRate = (value: number | null | undefined): string => {
    if (value === null || value === undefined) {
      return 'N/A';
    }
    const numValue = Number(value);
    if (numValue === 0) {
      return 'Free';
    }
    return `${currencySign}${numValue.toFixed(2)}`;
  };

  const getAllTiers = (method: SelectedMethodInfo): PriceRangeTier[] => {
    const tiers: PriceRangeTier[] = [];

    if (method.rate !== undefined) {
      tiers.push({
        minOrderPriceRange: method.minOrderPriceRange ?? null,
        maxOrderPriceRange: method.maxOrderPriceRange ?? null,
        rate: method.rate ?? 0,
      });
    }

    if (method.additionalPriceRanges && Array.isArray(method.additionalPriceRanges)) {
      tiers.push(...method.additionalPriceRanges);
    }

    return tiers;
  };

  const getAdjustedTiers = (tiers: PriceRangeTier[]): PriceRangeTier[] => {
    return tiers.map((tier) => ({
      ...tier,
      rate: adjustRate(tier.rate) ?? 0,
    }));
  };

  const hasTierChanges = (original: PriceRangeTier, adjusted: PriceRangeTier): boolean => {
    return Number(original.rate) !== Number(adjusted.rate);
  };

  const methodHasChanges = (method: SelectedMethodInfo): boolean => {
    const originalTiers = getAllTiers(method);
    if (originalTiers.length === 0) return false;
    const adjustedTiers = getAdjustedTiers(originalTiers);
    return originalTiers.some((orig, idx) => hasTierChanges(orig, adjustedTiers[idx]));
  };

  const changedMethods = selectedMethods.filter(methodHasChanges);
  const unchangedMethods = selectedMethods.filter((m) => !methodHasChanges(m));
  const affectedCount = changedMethods.length;
  const isSaveDisabled = affectedCount === 0;

  const formatTierRange = (tier: PriceRangeTier): string => {
    const min = tier.minOrderPriceRange;
    const max = tier.maxOrderPriceRange;

    const formatMin = min === null || min === undefined ? 'No limit' : `${currencySign}${Number(min).toFixed(2)}`;
    const formatMax =
      max === null || max === undefined || Number(max) === 0 ? 'No limit' : `${currencySign}${Number(max).toFixed(2)}`;

    return `${formatMin} - ${formatMax}`;
  };

  return (
    <div className="bulk-edit-modal__view">
      <div className="bulk-edit-modal__header bulk-edit-modal__header--with-subtitle">
        <div className="bulk-edit-modal__header__left">
          <div className="bulk-edit-modal__header__titles">
            <h2>Confirm changes</h2>
            <span className="bulk-edit-modal__header__subtitle">
              {affectedCount} of {selectedMethods.length} Methods Will Be Affected
            </span>
          </div>
        </div>
        <Button icon="close" variant="ghost" size="small" onClick={onClose} />
      </div>
      <div className="bulk-edit-modal__content">
        <div className="bulk-edit-modal__summary">
          <span className="bulk-edit-modal__summary__label">Rate Adjustment:</span>
          <span className="bulk-edit-modal__summary__value">
            {formData.adjustBy >= 0 ? '+' : ''}
            {currencySign}
            {formData.adjustBy.toFixed(2)}
          </span>
          {formData.keepFreeShippingFree && (
            <span className="bulk-edit-modal__summary__note">(keeping free shipping free)</span>
          )}
        </div>
        {changedMethods.length > 0 && (
          <CollapsibleSection title="Changed" count={changedMethods.length} defaultOpen={true}>
            {changedMethods.map((method) => {
              const originalTiers = getAllTiers(method);
              const adjustedTiers = getAdjustedTiers(originalTiers);

              return (
                <div key={method.id} className="bulk-edit-modal__preview-item">
                  <div className="bulk-edit-modal__preview-item__header">{method.name || '(No name)'}</div>
                  <div className="bulk-edit-modal__preview-item__group">{method.groupName}</div>
                  {originalTiers.map((originalTier, index) => {
                    const adjustedTier = adjustedTiers[index];
                    const hasChange = hasTierChanges(originalTier, adjustedTier);

                    return (
                      <div key={index} className="bulk-edit-modal__preview-item__change">
                        <span className="bulk-edit-modal__preview-item__change__label">
                          Tier {index + 1} ({formatTierRange(originalTier)}):
                        </span>
                        {hasChange ? (
                          <>
                            <span className="bulk-edit-modal__preview-item__change__old">
                              {formatRate(originalTier.rate)}
                            </span>
                            <span className="bulk-edit-modal__preview-item__change__arrow">&gt;</span>
                            <span className="bulk-edit-modal__preview-item__change__new">
                              {formatRate(adjustedTier.rate)}
                            </span>
                          </>
                        ) : (
                          <span className="bulk-edit-modal__preview-item__change__unchanged">
                            {formatRate(originalTier.rate)} (unchanged)
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </CollapsibleSection>
        )}
        {unchangedMethods.length > 0 && (
          <CollapsibleSection title="Unchanged" count={unchangedMethods.length} defaultOpen={false}>
            {unchangedMethods.map((method) => {
              const originalTiers = getAllTiers(method);

              if (originalTiers.length === 0) {
                return (
                  <div key={method.id} className="bulk-edit-modal__preview-item">
                    <div className="bulk-edit-modal__preview-item__header">{method.name || '(No name)'}</div>
                    <div className="bulk-edit-modal__preview-item__group">{method.groupName}</div>
                    <div className="bulk-edit-modal__preview-item__change">
                      <span className="bulk-edit-modal__preview-item__change__unchanged">No rates configured</span>
                    </div>
                  </div>
                );
              }

              return (
                <div key={method.id} className="bulk-edit-modal__preview-item">
                  <div className="bulk-edit-modal__preview-item__header">{method.name || '(No name)'}</div>
                  <div className="bulk-edit-modal__preview-item__group">{method.groupName}</div>
                  {originalTiers.map((originalTier, index) => (
                    <div key={index} className="bulk-edit-modal__preview-item__change">
                      <span className="bulk-edit-modal__preview-item__change__label">
                        Tier {index + 1} ({formatTierRange(originalTier)}):
                      </span>
                      <span className="bulk-edit-modal__preview-item__change__unchanged">
                        {formatRate(originalTier.rate)} (unchanged)
                      </span>
                    </div>
                  ))}
                </div>
              );
            })}
          </CollapsibleSection>
        )}
      </div>
      <div className="bulk-edit-modal__footer">
        <Button text="Back" onClick={onBack} size="medium" variant="secondary" />
        {isSaveDisabled ? (
          <Tooltip tooltipText="No methods will be affected by this change" maxWidth={220}>
            <Button
              text="Save Changes"
              onClick={() => onSaveChanges(changedMethods.map((m) => m.id))}
              size="medium"
              variant="primary"
              disabled
            />
          </Tooltip>
        ) : (
          <div className="bulk-edit-modal__button-wrapper">
            <Button
              text="Save Changes"
              onClick={() => onSaveChanges(changedMethods.map((m) => m.id))}
              size="medium"
              variant="primary"
            />
          </div>
        )}
      </div>
    </div>
  );
};
